import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, ImageOff, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusPill } from "@/components/StatusPill";
import { api, downloadFile, apiError } from "@/lib/api";
import { PUB_META, fmtEUR, STATUS_META } from "@/lib/constants";

const CHANNEL_NAMES = { mobile_de: "mobile.de", autoscout24: "AutoScout24", homepage: "Homepage", csv_export: "CSV", json_export: "JSON" };

export default function PublishingPage() {
  const [rows, setRows] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/publishing/overview").then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);

  const exportInventory = async () => {
    try {
      await downloadFile("/export/inventory.csv", "caros_bestand.csv");
      toast.success("Bestandsexport heruntergeladen");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3" data-tour="publishing-overview">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold">Publikationszentrale</h1>
          <p className="text-sm text-muted-foreground">Wo ist welches Fahrzeug online? Wo gibt es Fehler oder ausstehende Updates?</p>
        </div>
        <Button variant="outline" className="gap-2" onClick={exportInventory} data-testid="publishing-export-inventory">
          <Download className="h-4 w-4" /> Bestand als CSV
        </Button>
      </div>

      {!rows ? (
        <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div>
      ) : (
        <div className="space-y-2">
          {rows.map((v) => (
            <Card key={v.id} className="cursor-pointer card-hover" onClick={() => navigate(`/app/fahrzeuge/${v.id}?tab=publish`)} data-testid={`publishing-row-${v.bestandsnummer}`}>
              <CardContent className="py-3 flex flex-wrap items-center gap-3">
                {v.cover_image_url ? (
                  <img src={v.cover_image_url} alt="" className="h-12 w-16 rounded object-cover" loading="lazy" />
                ) : (
                  <div className="h-12 w-16 rounded bg-secondary flex items-center justify-center"><ImageOff className="h-4 w-4 text-muted-foreground" /></div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{v.marke} {v.modell} <span className="text-muted-foreground font-normal">· {fmtEUR(v.price)}</span></div>
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    <StatusPill label={STATUS_META[v.status]?.label || v.status} color={STATUS_META[v.status]?.color} />
                    {Object.entries(v.publications || {}).map(([ch, p]) => (
                      <StatusPill key={ch} label={`${CHANNEL_NAMES[ch] || ch}: ${PUB_META[p.status]?.label || p.status}`}
                        color={PUB_META[p.status]?.color} hint={p.last_error || p.external_url} />
                    ))}
                    {Object.keys(v.publications || {}).length === 0 && (
                      <span className="text-xs text-muted-foreground">Noch auf keinem Kanal</span>
                    )}
                  </div>
                </div>
                <ExternalLink className="h-4 w-4 text-muted-foreground shrink-0" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
